import { ArrowDown, ArrowUp, ArrowUpDown, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface SortOption<K extends string> {
  key: K;
  label: string;
}

interface SortDialogProps<K extends string> {
  options: SortOption<K>[];
  sortBy: K | undefined;
  sortDir: 'asc' | 'desc';
  onChange: (key: K) => void;
  onClear: () => void;
}

export function SortDialog<K extends string>({
  options,
  sortBy,
  sortDir,
  onChange,
  onClear,
}: SortDialogProps<K>) {
  const [open, setOpen] = useState(false);

  const icon = (key: K) => {
    if (sortBy !== key) return <ArrowUpDown className="h-3.5 w-3.5" />;
    return sortDir === 'asc' ? (
      <ArrowUp className="h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" />
    );
  };

  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen(true)}>
        <ArrowUpDown className="h-4 w-4" />
        Sort
        {sortBy && (
          <span className="ml-1 rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
            1
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Sort by</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            {options.map((opt) => (
              <Button
                key={opt.key}
                variant={sortBy === opt.key ? 'default' : 'outline'}
                aria-pressed={sortBy === opt.key}
                size="sm"
                className="justify-start gap-1.5"
                onClick={() => onChange(opt.key)}
              >
                {icon(opt.key)}
                {opt.label}
              </Button>
            ))}
          </div>

          <DialogFooter>
            {sortBy && (
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground"
                onClick={onClear}
              >
                <X className="h-3.5 w-3.5" />
                Clear sort
              </Button>
            )}
            <Button size="sm" onClick={() => setOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
